/*
  Rococo Sand - collection price filter for every market.

  Shopify only offers the native price_range filter where a market's prices are
  fixed rather than exchange rate converted. On this store that is the USA/USD
  market alone, so shoppers on INR / GBP / AED / CAD / AUD got no price filter at
  all - and filter.v.price.* params are ignored server side there, so nothing in
  Liquid can bring it back.

  This rebuilds the same filter wherever Shopify does not ship one:

    - prices for the whole (already filtered) collection come from
      templates/collection.rs-prices.liquid   ->  ?view=rs-prices&page=N
      in the ACTIVE presentment currency, as integers in minor units.
    - the slider is the theme's own theme.PriceRange / noUiSlider instance, so
      it looks and formats money exactly like the native one does in USD.
    - matching products are assembled from the real section render
      (?section_id=<id>&page=N), so the cards are the same markup the server
      would have sent - no hand built cards, no missing markup.
    - the chosen range lives in the URL as rs_pmin / rs_pmax. Those ride along
      in the theme's own filter FormData, so they survive its AJAX re-render,
      and are re-applied on every collection:reloaded.

  It no-ops completely when Shopify does render a native price filter (USD), so
  that market keeps its fully server side filtering.
*/
(function () {
  'use strict';

  var MIN_PARAM = 'rs_pmin';
  var MAX_PARAM = 'rs_pmax';
  var DROPPED = ['page', 'view', 'section_id', MIN_PARAM, MAX_PARAM];

  var SEL = {
    form: '.filter-form',
    section: '[data-section-type="collection-grid"]',
    grid: '.collection-grid__wrapper .grid--uniform',
    count: '.collection-filter__item--count',
    pagination: '.pagination',
    nativePrice: '[name="filter.v.price.gte"]',
    card: '[data-product-handle]'
  };

  var feedCache = {};
  var cardCache = {};
  var priceRange = null;
  var replaced = false;
  var stylesAdded = false;

  function qs(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function qsa(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  /* ---------------------------------------------------------------- urls -- */

  function baseParams() {
    var params = new URLSearchParams(window.location.search);
    DROPPED.forEach(function (key) {
      params.delete(key);
    });
    return params;
  }

  function cacheKey() {
    return baseParams().toString();
  }

  function urlWith(extra) {
    var params = baseParams();
    Object.keys(extra).forEach(function (key) {
      params.set(key, extra[key]);
    });
    return window.location.pathname + '?' + params.toString();
  }

  function activeRange() {
    var params = new URLSearchParams(window.location.search);
    var min = parseFloat(params.get(MIN_PARAM));
    var max = parseFloat(params.get(MAX_PARAM));
    if (isNaN(min) && isNaN(max)) return null;
    return { min: isNaN(min) ? 0 : min, max: isNaN(max) ? Infinity : max };
  }

  /* --------------------------------------------------------------- fetch -- */

  // handle -> price in major units, for the collection as currently filtered.
  function loadPrices() {
    var key = cacheKey();
    if (feedCache[key]) return feedCache[key];

    feedCache[key] = getJSON(urlWith({ view: 'rs-prices', page: 1 }))
      .then(function (first) {
        var rest = [];
        for (var page = 2; page <= (first.__pages || 1); page++) {
          rest.push(getJSON(urlWith({ view: 'rs-prices', page: page })));
        }
        return Promise.all(rest).then(function (chunks) {
          var prices = {};
          [first].concat(chunks).forEach(function (chunk) {
            var map = (chunk && chunk.prices) || {};
            Object.keys(map).forEach(function (handle) {
              prices[handle] = map[handle] / 100;
            });
          });
          return { prices: prices, total: first.__total || 0 };
        });
      })
      .catch(function (err) {
        delete feedCache[key];
        throw err;
      });

    return feedCache[key];
  }

  // Every product card of the collection as currently filtered, in grid order.
  function loadCards() {
    var key = cacheKey();
    if (cardCache[key]) return cardCache[key];

    var section = qs(SEL.section);
    var sectionId = section && section.getAttribute('data-section-id');
    if (!sectionId) return Promise.reject(new Error('rs-price-filter: no section id'));

    function fetchPage(page) {
      return fetch(urlWith({ section_id: sectionId, page: page }), { credentials: 'same-origin' })
        .then(function (response) {
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          return qsa(SEL.card, doc).map(function (card) {
            qsa('script', card).forEach(function (node) {
              node.parentNode.removeChild(node);
            });
            return { handle: card.getAttribute('data-product-handle'), html: card.outerHTML };
          });
        });
    }

    cardCache[key] = Promise.all([fetchPage(1), loadPrices()])
      .then(function (results) {
        var first = results[0];
        var total = results[1].total;
        var perPage = first.length;
        var pages = [];
        var last = perPage ? Math.ceil(total / perPage) : 1;
        for (var page = 2; page <= last; page++) pages.push(fetchPage(page));

        return Promise.all(pages).then(function (chunks) {
          var list = first.slice();
          chunks.forEach(function (chunk) {
            list = list.concat(chunk);
          });
          return { perPage: perPage || list.length, list: list };
        });
      })
      .catch(function (err) {
        delete cardCache[key];
        throw err;
      });

    return cardCache[key];
  }

  function getJSON(url) {
    return fetch(url, { credentials: 'same-origin' }).then(function (response) {
      return response.json();
    });
  }

  /* ------------------------------------------------------------------ ui -- */

  function addStyles() {
    if (stylesAdded) return;
    stylesAdded = true;
    var style = document.createElement('style');
    style.textContent =
      '.rs-price-loading{opacity:.35;pointer-events:none;transition:opacity .2s}' +
      '.rs-price-empty{width:100%;padding:40px 0;text-align:center}';
    document.head.appendChild(style);
  }

  // A power of ten roughly a hundredth of the span, so the slider feels the
  // same whether the market counts in dollars or in rupees.
  function stepFor(span) {
    var raw = Math.max(span / 100, 1);
    return Math.pow(10, Math.floor(Math.log(raw) / Math.LN10));
  }

  function buildGroup(min, max, value) {
    var wrapper = document.createElement('div');
    wrapper.className = 'collection-sidebar__group--rs-price rs-price-group';
    wrapper.innerHTML =
      '<div class="collection-sidebar__group">' +
        '<button type="button" class="collapsible-trigger collapsible-trigger-btn collapsible--auto-height is-open tag-list__header" aria-controls="RsPriceFilter" data-collapsible-id="filter-price">' +
          'Price' +
          '<span class="collapsible-trigger__icon collapsible-trigger__icon--open" role="presentation">' +
            '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon--wide icon-chevron-down" viewBox="0 0 28 16"><path d="m1.57 1.59 12.76 12.77L27.1 1.59" stroke-width="2" stroke="#000" fill="none"/></svg>' +
          '</span>' +
        '</button>' +
        '<div id="RsPriceFilter" class="collapsible-content collapsible-content--sidebar is-open" data-collapsible-id="filter-price" style="height: auto;">' +
          '<div class="collapsible-content__inner">' +
            '<div class="price-range" data-min-value="' + value.min + '" data-min-name="' + MIN_PARAM + '" data-min="' + min + '" data-max-value="' + value.max + '" data-max-name="' + MAX_PARAM + '" data-max="' + max + '">' +
              '<div class="price-range__display-wrapper">' +
                '<span class="price-range__display-min"></span>' +
                '<span class="price-range__display-max"></span>' +
              '</div>' +
              '<div class="price-range__slider-wrapper"><div class="price-range__slider"></div></div>' +
              '<input class="price-range__input price-range__input-min" name="' + MIN_PARAM + '" value="' + (value.set ? value.min : '') + '" readonly>' +
              '<input class="price-range__input price-range__input-max" name="' + MAX_PARAM + '" value="' + (value.set ? value.max : '') + '" readonly>' +
            '</div>' +
          '</div>' +
        '</div>' +
      '</div>';
    return wrapper;
  }

  // Sit where the native filter sits in USD: after Size, before Availability.
  function insertGroup(form, group) {
    var availability = qs('[name="filter.v.availability"]', form);
    var anchor = availability && availability.closest('[class*="collection-sidebar__group--"]');
    if (anchor && anchor.parentNode === form) {
      form.insertBefore(group, anchor);
    } else {
      form.appendChild(group);
    }
  }

  function injectUI(prices) {
    var values = [];
    Object.keys(prices).forEach(function (handle) {
      if (typeof prices[handle] === 'number' && !isNaN(prices[handle])) values.push(prices[handle]);
    });
    if (values.length < 2) return;

    var min = Math.floor(Math.min.apply(null, values));
    var max = Math.ceil(Math.max.apply(null, values));
    if (max <= min) return;

    var range = activeRange();
    var value = {
      set: !!range,
      min: range ? Math.max(min, range.min) : min,
      max: range && isFinite(range.max) ? Math.min(max, range.max) : max
    };

    qsa(SEL.form).forEach(function (form) {
      qsa('.rs-price-group', form).forEach(function (old) {
        old.parentNode.removeChild(old);
      });
      insertGroup(form, buildGroup(min, max, value));
    });

    var container = qs('.rs-price-group .price-range');
    if (!container) return;

    try {
      priceRange = new theme.PriceRange(container, {
        step: stepFor(max - min),
        onChange: function () {
          onSliderChange(container, min, max);
        }
      });
    } catch (err) {
      qsa('.rs-price-group').forEach(function (node) {
        node.parentNode.removeChild(node);
      });
      return;
    }

    if (window.theme && theme.collapsibles && theme.collapsibles.init) theme.collapsibles.init();
  }

  function onSliderChange(container, min, max) {
    var lo = parseFloat(qs('.price-range__input-min', container).value);
    var hi = parseFloat(qs('.price-range__input-max', container).value);
    var params = new URLSearchParams(window.location.search);

    if (isNaN(lo) || isNaN(hi) || (lo <= min && hi >= max)) {
      params.delete(MIN_PARAM);
      params.delete(MAX_PARAM);
      qs('.price-range__input-min', container).value = '';
      qs('.price-range__input-max', container).value = '';
    } else {
      params.set(MIN_PARAM, lo);
      params.set(MAX_PARAM, hi);
    }

    var search = params.toString();
    history.replaceState({}, '', window.location.pathname + (search ? '?' + search : ''));
    render();
  }

  /* -------------------------------------------------------------- render -- */

  // The theme parks .image-element at opacity 0 and lets AOS reveal it, so a
  // card that is inserted rather than rendered would show an empty frame until
  // AOS happened to notice it. Injected cards arrive already marked animated.
  function markAnimated(el) {
    if (el.hasAttribute('data-aos')) el.classList.add('aos-init', 'aos-animate');
  }

  function paintCards(grid, list) {
    var holder = document.createElement('div');
    var fragment = document.createDocumentFragment();

    list.forEach(function (item) {
      holder.innerHTML = item.html;
      var card = holder.firstElementChild;
      if (!card) return;
      markAnimated(card);
      qsa('[data-aos]', card).forEach(markAnimated);
      fragment.appendChild(card);
    });

    grid.innerHTML = '';
    grid.appendChild(fragment);
    if (window.theme && theme.reinitProductGridItem) theme.reinitProductGridItem();
  }

  function setCount(grid, total) {
    var el = qs(SEL.count);
    if (!el) return;
    if (!el.dataset.rsLabel) {
      var label = el.textContent.trim().replace(/^[\d.,]+\s*/, '');
      el.dataset.rsLabel = label || 'products';
    }
    el.textContent = total + ' ' + el.dataset.rsLabel;
  }

  function setPagination(hidden) {
    qsa(SEL.pagination).forEach(function (el) {
      el.style.display = hidden ? 'none' : '';
    });
  }

  function setEmpty(grid, show) {
    var existing = qs('.rs-price-empty', grid);
    if (!show) {
      if (existing) existing.parentNode.removeChild(existing);
      return;
    }
    if (existing) return;
    var el = document.createElement('div');
    el.className = 'rs-price-empty';
    el.textContent = 'No products in this price range.';
    grid.appendChild(el);
  }

  function render() {
    var grid = qs(SEL.grid);
    if (!grid) return;

    var range = activeRange();
    if (!range) {
      restore(grid);
      return;
    }

    grid.classList.add('rs-price-loading');
    Promise.all([loadPrices(), loadCards()])
      .then(function (results) {
        var prices = results[0].prices;
        var cards = results[1].list;
        var matched = cards.filter(function (card) {
          var price = prices[card.handle];
          return typeof price === 'number' && price >= range.min - 0.005 && price <= range.max + 0.005;
        });

        paintCards(grid, matched);
        setEmpty(grid, matched.length === 0);
        setCount(grid, matched.length);
        setPagination(true);
        replaced = true;
      })
      .catch(function (err) {
        if (window.console) console.warn('rs-price-filter:', err);
      })
      .then(function () {
        grid.classList.remove('rs-price-loading');
      });
  }

  function restore(grid) {
    if (!replaced) return;
    replaced = false;
    grid.classList.add('rs-price-loading');

    Promise.all([loadPrices(), loadCards()])
      .then(function (results) {
        var total = results[0].total;
        var cards = results[1];
        var page = parseInt(new URLSearchParams(window.location.search).get('page') || '1', 10) || 1;
        var start = (page - 1) * cards.perPage;

        setEmpty(grid, false);
        paintCards(grid, cards.list.slice(start, start + cards.perPage));
        setCount(grid, total);
        setPagination(false);
      })
      .catch(function () {
        window.location.href = urlWith({});
      })
      .then(function () {
        grid.classList.remove('rs-price-loading');
      });
  }

  /* ---------------------------------------------------------------- boot -- */

  // The card's own size buttons are bound by an inline DOMContentLoaded script,
  // so any card that arrives later - from the theme's AJAX filtering or from
  // here - would have a dead size row. One delegated listener covers them all.
  function bindSizeButtons() {
    document.addEventListener('click', function (evt) {
      var btn = evt.target && evt.target.closest && evt.target.closest('.grid-size-btn');
      if (!btn) return;
      evt.preventDefault();

      var card = btn.closest('.grid-product__content');
      if (!card) return;

      qsa('.grid-size-btn', card).forEach(function (sibling) {
        sibling.classList.remove('selected');
      });
      btn.classList.add('selected');

      var input = qs('.grid-product-variant-id', card);
      if (input) input.value = btn.getAttribute('data-variant-id');
    });
  }

  function init() {
    if (window.location.pathname.indexOf('/collections/') !== 0) return;
    if (!qs(SEL.form) || !qs(SEL.grid)) return;
    if (qs(SEL.form + ' ' + SEL.nativePrice)) return; // Shopify ships one here
    if (!window.theme || !theme.PriceRange || typeof window.noUiSlider === 'undefined') return;

    addStyles();
    replaced = false;

    loadPrices()
      .then(function (feed) {
        injectUI(feed.prices);
        render();
      })
      .catch(function (err) {
        if (window.console) console.warn('rs-price-filter:', err);
      });
  }

  bindSizeButtons();
  document.addEventListener('collection:reloaded', init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
